import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import PDFTemplate from '@/features/PDFTemplate';
import type {
  ServiceUsagePerDayDTO,
  UsageLimitType
} from '@/pages/Dashboard/Dashboard.types.ts';

import { type Organization, organizationsAtom } from '../../Organizations';
import { mappedTablePages } from './ReportsPages/CasesPages/CasesPages.utils.tsx';
import {
  CasesPage1,
  EventsPage1,
  EventsPage2,
  EventsPage3,
  EventsPage4,
  EventsPage5,
  ExecutiveReportPage2,
  LogUsagePage,
  NetworkUsagePage,
  ReportsPageCover,
  ReportsPageExecFunnel,
  ReportsPageInfo,
  ReportsPageSocInsights,
  TicketsPage1,
  TicketsPage2,
  TicketsPage3
} from './ReportsPages/index.ts';
import ReportsPageGlossary from './ReportsPages/ReportsPageGlossary.tsx';
import { TicketsMetrics } from './ReportsPages/TicketsPages/index.ts';
import {
  reportsEndDateAtom,
  reportsFrequencyAtom,
  reportsOrgLabelAtom,
  reportsStartDateAtom,
  reportsTimeFrameAtom,
  useReportData
} from './ReportsTemplate.hooks';
import {
  formatDateRange,
  getTemplateValues,
  getURLTimeframeData
} from './ReportsTemplate.utils.tsx';

// DEFAULT MDR REPORTS TEMPLATE
const ReportsTemplate = () => {
  const [organizations] = useAtom(organizationsAtom);
  const [, setReportsOrg] = useAtom(reportsOrgLabelAtom);
  const [, setReportsStartDate] = useAtom(reportsStartDateAtom);
  const [, setReportsEndDate] = useAtom(reportsEndDateAtom);
  const [, setReportsFrequency] = useAtom(reportsFrequencyAtom);
  const [, setReportsTimeFrame] = useAtom(reportsTimeFrameAtom);

  const token = new URLSearchParams(window.location.search).get('token'); // Get token from URL
  const organizationLabel =
    new URLSearchParams(window.location.search).get('organization') ?? 'pontst'; // Get organization from URL
  const startDate =
    new URLSearchParams(window.location.search).get('start') ?? '2024-12-01'; // Get startDate from URL
  const endDate =
    new URLSearchParams(window.location.search).get('end') ?? '2024-12-31'; // Get endDate from URL
  const reportFrequency =
    new URLSearchParams(window.location.search).get('report_frequency') ??
    'Monthly'; // Get reportFrequency from URL

  const diff = getTemplateValues();

  useEffect(() => {
    setReportsOrg(organizationLabel);
    setReportsStartDate(startDate);
    setReportsEndDate(endDate);
    setReportsFrequency(reportFrequency);
    setReportsTimeFrame(diff);
  }, [organizationLabel, startDate, endDate, reportFrequency]);

  const organization = organizations.find(
    (org) => org.label === organizationLabel
  ) as Organization; // Get organization name from URL, fallback to demo1 org

  // TODO: We should get the reportName from the url
  const reportName = 'MDR Executive Report'; // Get report name from URL

  const validToken = 'valid_token'; // Mocked token

  const {
    eventsPerDay,
    usageData,
    usageLimitsData,
    productsData,
    fetchReportData,
    tickets,
    orgAllMetrics,
    insights,
    casesPerDay,
    ticketedCasesPerDay,
    logCasesTableData,
    networkCasesTableData,
    logTicketedCasesTableData,
    networkTicketedCasesTableData,
    isFetchingTickets
  } = useReportData({ organization, organizationLabel, startDate, endDate });

  const activeProductTypes = useMemo(() => {
    if (!productsData || productsData.length === 0) return [];

    const types = productsData
      .filter((product) => product.active)
      .map((product) => product.product_type);

    // Ensure uniqueness
    return Array.from(new Set(types));
  }, [productsData]);

  const hasLog = activeProductTypes.includes('LOG');
  const hasNetwork = activeProductTypes.includes('NET');
  const hasEDR = activeProductTypes.includes('EDR');

  // UseEffect to fetch data
  useEffect(() => {
    if (token !== validToken) {
      // Invalid token - do not fetch data
      return;
    }

    fetchReportData();
  }, []);

  // UseEffect to set DOM ready marker when all data is loaded and report is ready for PDF generation
  useEffect(() => {
    // Check if all critical data is loaded
    const isDataLoaded =
      casesPerDay.length >= 0 && // Can be empty array but should be defined
      eventsPerDay.length >= 0 && // Can be empty array but should be defined
      usageData &&
      usageLimitsData &&
      tickets && // Can be empty array but should be defined
      activeProductTypes.length >= 0 && // Can be empty but should be defined
      ticketedCasesPerDay.length >= 0 && // Can be empty array but should be defined
      networkCasesTableData.length >= 0 && // Can be empty array but should be defined
      logCasesTableData.length >= 0 && // Can be empty array but should be defined
      networkTicketedCasesTableData.length >= 0 && // Can be empty array but should be defined
      logTicketedCasesTableData.length >= 0 && // Can be empty array but should be defined
      !isFetchingTickets; // Ensure tickets are not still being fetched

    if (isDataLoaded) {
      // Add a small delay to ensure all React renders are complete
      const timer = setTimeout(() => {
        // Set DOM marker that Playwright can wait for
        document.body.setAttribute('data-report-ready', 'true');
        // Report data loaded and DOM ready for PDF generation
      }, 1000); // 1 second delay to allow for chart rendering

      return () => clearTimeout(timer);
    }

    // Return undefined explicitly when data is not loaded
    return undefined;
  }, [
    casesPerDay,
    eventsPerDay,
    usageData,
    usageLimitsData,
    tickets,
    ticketedCasesPerDay,
    networkCasesTableData,
    logCasesTableData,
    networkTicketedCasesTableData,
    logTicketedCasesTableData,
    isFetchingTickets,
    casesPerDay,
    activeProductTypes,
    insights,
    networkCasesTableData,
    logCasesTableData,
    networkTicketedCasesTableData,
    logTicketedCasesTableData
  ]);

  useEffect(() => {
    document.documentElement.classList.add('pdf-mode');
    document.body.classList.add('pdf-mode');

    return () => {
      document.documentElement.classList.remove('pdf-mode');
      document.body.classList.remove('pdf-mode');
    };
  }, []);

  // --------- Because when diff is less than 7 days (aka daily reports) we need to show single day data for cases and events, we need to correct the data accordingly ---------//
  // These vars correct the data (we are pulling for 7 days but only want the single day data for cases and events)
  const singleDayEvents = getURLTimeframeData(eventsPerDay, 'date');

  const singleDayTicketedCases = getURLTimeframeData(
    ticketedCasesPerDay,
    'value'
  );
  const singleDayCases = getURLTimeframeData(casesPerDay, 'value');
  const singleDayTickets = getURLTimeframeData(tickets, 'published_at');

  const timeFrameCorrectedEvents = diff < 7 ? singleDayEvents : eventsPerDay;
  const timeFrameCorrectedTickets = diff < 7 ? singleDayTickets : tickets;
  const timeFrameCorrectedTicketedCases =
    diff < 7 ? singleDayTicketedCases : ticketedCasesPerDay;
  const timeFrameCorrectedCases = diff < 7 ? singleDayCases : casesPerDay;
  // ------------------------------------------------------------------------------------------------//

  const reportPages = [
    {
      key: 'Executive Summary - Funnel', // key not used yet, just for identification for now
      sectionLabel: 'Executive Summary',
      variant: 'report',
      visible: true,
      content: (
        <ReportsPageExecFunnel
          activeProductTypes={activeProductTypes as string[]}
          diff={diff}
          usageDataRaw={usageData as ServiceUsagePerDayDTO[]}
          usageLimitsDataRaw={usageLimitsData as UsageLimitType[]}
          eventsDataRaw={timeFrameCorrectedEvents}
          casesDataRaw={timeFrameCorrectedCases.map(
            (caseItem) => caseItem.count || 0
          )}
          ticketedCases={timeFrameCorrectedTicketedCases}
          ticketsDataRaw={timeFrameCorrectedTickets}
        />
      )
    },
    {
      key: 'Executive Summary - Key Metrics',
      sectionLabel: 'Executive Summary',
      variant: 'report',
      content: (
        <ExecutiveReportPage2
          newTickets={timeFrameCorrectedTickets}
          orgAllMetrics={orgAllMetrics}
        />
      ),
      visible: true
    },
    {
      key: 'Executive Summary - SOC Insights',
      sectionLabel: 'Executive Summary',
      variant: 'report',
      visible: (insights?.body_1 || insights?.body_2) && diff > 7,
      content: <ReportsPageSocInsights insights={insights!} />
    },
    {
      key: 'Tickets1',
      sectionLabel: 'Threat Tickets',
      variant: 'report',
      content: <TicketsPage1 tickets={tickets} />,
      visible: true
    },
    {
      key: 'Tickets2',
      sectionLabel: 'Threat Tickets',
      variant: 'report',
      content: (
        <TicketsPage2
          tickets={timeFrameCorrectedTickets}
          ticketedCases={timeFrameCorrectedTicketedCases}
        />
      ),
      visible: true
    },
    {
      key: 'TicketsMetrics',
      sectionLabel: 'Threat Tickets',
      variant: 'report',
      content: <TicketsMetrics orgAllMetrics={orgAllMetrics} />,
      visible: true
    },
    {
      key: 'Tickets3',
      sectionLabel: 'Service Tickets',
      variant: 'report',
      content: <TicketsPage3 />,
      visible: true
    },
    {
      key: 'Cases1',
      sectionLabel: 'Cases',
      variant: 'report',
      visible: true,
      content: (
        <CasesPage1
          hasLog={hasLog}
          hasNetwork={hasNetwork}
          hasEDR={hasEDR}
          ticketedCasesPerDay={ticketedCasesPerDay}
          casesPerDay={casesPerDay}
        />
      )
    },
    {
      key: 'LogCases',
      sectionLabel: 'Cases',
      variant: 'report',
      content: <></>,
      visible: hasLog || hasEDR
    },
    {
      key: 'TicketedLogCases',
      sectionLabel: 'Cases',
      variant: 'report',
      content: <></>,
      visible: hasLog || hasEDR
    },
    {
      key: 'NetworkCases',
      sectionLabel: 'Cases',
      variant: 'report',
      content: <></>,
      visible: hasNetwork
    },
    {
      key: 'TicketedNetworkCases',
      sectionLabel: 'Cases',
      variant: 'report',
      content: <></>,
      visible: hasNetwork
    },
    {
      key: 'Events1',
      sectionLabel: 'Events',
      variant: 'report',
      visible: true,
      content: (
        <EventsPage1
          eventsDataRaw={eventsPerDay}
          hasLog={hasLog}
          hasNetwork={hasNetwork}
          hasEDR={hasEDR}
        />
      )
    },
    {
      key: 'Events2',
      sectionLabel: 'Events',
      variant: 'report',
      visible: hasLog,
      content: <EventsPage2 />
    },
    {
      key: 'Events3',
      sectionLabel: 'Events',
      variant: 'report',
      visible: hasLog,
      content: <EventsPage3 />
    },
    {
      key: 'Events4',
      sectionLabel: 'Events',
      variant: 'report',
      visible: hasNetwork,
      content: <EventsPage4 />
    },
    {
      key: 'Events5',
      sectionLabel: 'Events',
      variant: 'report',
      visible: hasNetwork,
      content: <EventsPage5 />
    },
    {
      key: 'LogUsagePage',
      sectionLabel: 'Usage',
      visible: hasLog,
      variant: 'report',
      content: <LogUsagePage />
    },
    {
      key: 'NetworkUsagePage',
      sectionLabel: 'Usage',
      visible: hasNetwork,
      variant: 'report',
      content: <NetworkUsagePage />
    },
    {
      key: 'Glossary',
      sectionLabel: 'Glossary',
      variant: 'report',
      visible: true,
      content: <ReportsPageGlossary />
    }
  ];

  let runningPageNumber = 1;

  return (
    <div>
      <PDFTemplate variant="cover">
        <ReportsPageCover
          reportName={reportName}
          organizationName={organization?.name}
          reportFrequency={reportFrequency}
        />
      </PDFTemplate>
      {reportPages
        .filter((page) => page.visible)
        .map((page) => {
          // For sections that expand into multiple pages
          if (page.key === 'NetworkCases') {
            if (networkCasesTableData.length === 0) return null;
            const pages = mappedTablePages(networkCasesTableData, 'network');
            const rendered = pages.map((tablePage, tableIndex) => (
              <PDFTemplate
                key={`${page.key}-${uuidv4()}`}
                variant={page.variant}
                headerTitle="MDR Executive Report"
                headerClientLabel={organization?.name}
                headerDateLabel={formatDateRange(reportFrequency)}
                headerSectionLabel={page.sectionLabel}
                page_number={runningPageNumber + tableIndex}
                dataTestId={`${page.key}-page-${tableIndex + 1}`}
              >
                {tablePage}
              </PDFTemplate>
            ));
            runningPageNumber += pages.length;
            return rendered;
          }
          if (page.key === 'LogCases') {
            if (logCasesTableData.length === 0) return null;
            const pages = mappedTablePages(logCasesTableData, 'log');
            const rendered = pages.map((tablePage, tableIndex) => (
              <PDFTemplate
                key={`${page.key}-${uuidv4()}`}
                variant={page.variant}
                headerTitle="MDR Executive Report"
                headerClientLabel={organization?.name}
                headerDateLabel={formatDateRange(reportFrequency)}
                headerSectionLabel={page.sectionLabel}
                page_number={runningPageNumber + tableIndex}
                dataTestId={`${page.key}-page-${tableIndex + 1}`}
              >
                {tablePage}
              </PDFTemplate>
            ));
            runningPageNumber += pages.length;
            return rendered;
          }
          if (page.key === 'TicketedNetworkCases') {
            if (networkTicketedCasesTableData.length === 0) return null;
            const pages = mappedTablePages(
              networkTicketedCasesTableData,
              'ticketed network'
            );
            const rendered = pages.map((tablePage, tableIndex) => (
              <PDFTemplate
                key={`${page.key}-${uuidv4()}`}
                variant={page.variant}
                headerTitle="MDR Executive Report"
                headerClientLabel={organization?.name}
                headerDateLabel={formatDateRange(reportFrequency)}
                headerSectionLabel={page.sectionLabel}
                page_number={runningPageNumber + tableIndex}
                dataTestId={`${page.key}-page-${tableIndex + 1}`}
              >
                {tablePage}
              </PDFTemplate>
            ));
            runningPageNumber += pages.length;
            return rendered;
          }
          if (page.key === 'TicketedLogCases') {
            if (logTicketedCasesTableData.length === 0) return null;
            const pages = mappedTablePages(
              logTicketedCasesTableData,
              'ticketed log'
            );
            const rendered = pages.map((tablePage, tableIndex) => (
              <PDFTemplate
                key={`${page.key}-${uuidv4()}`}
                variant={page.variant}
                headerTitle="MDR Executive Report"
                headerClientLabel={organization?.name}
                headerDateLabel={formatDateRange(reportFrequency)}
                headerSectionLabel={page.sectionLabel}
                page_number={runningPageNumber + tableIndex}
                dataTestId={`${page.key}-page-${tableIndex + 1}`}
              >
                {tablePage}
              </PDFTemplate>
            ));
            runningPageNumber += pages.length;
            return rendered;
          }
          if (page.key === 'LogCases') {
            if (logCasesTableData.length === 0) return null;
            const pages = mappedTablePages(logCasesTableData, 'log');
            const rendered = pages.map((tablePage, tableIndex) => (
              <PDFTemplate
                key={`${page.key}-${uuidv4()}`}
                variant={page.variant}
                headerTitle="MDR Executive Report"
                headerClientLabel={organization?.name}
                headerDateLabel={formatDateRange(reportFrequency)}
                headerSectionLabel={page.sectionLabel}
                page_number={runningPageNumber + tableIndex}
                dataTestId={`${page.key}-page-${tableIndex + 1}`}
              >
                {tablePage}
              </PDFTemplate>
            ));
            runningPageNumber += pages.length;
            return rendered;
          }
          if (page.key === 'TicketedNetworkCases') {
            if (networkTicketedCasesTableData.length === 0) return null;
            const pages = mappedTablePages(
              networkTicketedCasesTableData,
              'ticketed network'
            );
            const rendered = pages.map((tablePage, tableIndex) => (
              <PDFTemplate
                key={`${page.key}-${uuidv4()}`}
                variant={page.variant}
                headerTitle="MDR Executive Report"
                headerClientLabel={organization?.name}
                headerDateLabel={formatDateRange(reportFrequency)}
                headerSectionLabel={page.sectionLabel}
                page_number={runningPageNumber + tableIndex}
                dataTestId={`${page.key}-page-${tableIndex + 1}`}
              >
                {tablePage}
              </PDFTemplate>
            ));
            runningPageNumber += pages.length;
            return rendered;
          }
          if (page.key === 'TicketedLogCases') {
            if (logTicketedCasesTableData.length === 0) return null;
            const pages = mappedTablePages(
              logTicketedCasesTableData,
              'ticketed log'
            );
            const rendered = pages.map((tablePage, tableIndex) => (
              <PDFTemplate
                key={`${page.key}-${uuidv4()}`}
                variant={page.variant}
                headerTitle="MDR Executive Report"
                headerClientLabel={organization?.name}
                headerDateLabel={formatDateRange(reportFrequency)}
                headerSectionLabel={page.sectionLabel}
                page_number={runningPageNumber + tableIndex}
                dataTestId={`${page.key}-page-${tableIndex + 1}`}
              >
                {tablePage}
              </PDFTemplate>
            ));
            runningPageNumber += pages.length;
            return rendered;
          }

          // For single-page sections
          const rendered = (
            <PDFTemplate
              key={page.key}
              variant={page.variant}
              headerTitle="MDR Executive Report"
              headerClientLabel={organization?.name}
              headerDateLabel={formatDateRange(reportFrequency)}
              headerSectionLabel={page.sectionLabel}
              page_number={runningPageNumber}
              dataTestId={`${page.key}-page`}
            >
              {page.content}
            </PDFTemplate>
          );
          runningPageNumber += 1;
          return rendered;
        })}

      <PDFTemplate variant="cover">
        <ReportsPageInfo
          reportName={reportName}
          organizationName={organization?.name}
        />
      </PDFTemplate>
    </div>
  );
};

export default ReportsTemplate;
